import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  PermisoAccion,
  PermisoSistema,
  SistemaCodigo,
} from '../models/sistema.interface';
import { SistemaService } from './sistema.service';
import { SistemaContextService } from './sistema-context.service';

@Injectable({ providedIn: 'root' })
export class PermisosStateService {
  private static readonly ADMIN_BLISTER_GROUP_CODE = 'admin_blister';

  private readonly storageKey = 'permisos_cache';
  private readonly permisosSubject = new BehaviorSubject<PermisoSistema | null>(
    null,
  );
  private readonly gruposSubject = new BehaviorSubject<any[]>([]);
  private readonly usuarioSubject = new BehaviorSubject<any | null>(null);
  private empresaIdActual: string | null = null;

  readonly permisos$: Observable<PermisoSistema | null> =
    this.permisosSubject.asObservable();
  readonly grupos$: Observable<any[]> = this.gruposSubject.asObservable();
  readonly usuario$: Observable<any | null> = this.usuarioSubject.asObservable();

  constructor(
    private readonly sistemaService: SistemaService,
    private readonly sistemaContext: SistemaContextService,
  ) {
    this.hidratarDesdeStorage();
  }

  cargarPermisos(codigo: SistemaCodigo, empresaId?: string | null) {
    this.empresaIdActual = empresaId ?? null;
    return this.sistemaService
      .obtenerPermisos(codigo, empresaId ?? undefined)
      .pipe(
        tap((resp) => {
          const gruposDesdeResp = this.extraerGruposDesdePermisos(resp as any);
          const usuarioDesdeResp = this.extraerUsuarioDesdePermisos(resp as any);

          this.guardarEnStorage(
            resp,
            this.empresaIdActual,
            gruposDesdeResp.length ? gruposDesdeResp : this.gruposSubject.value,
            usuarioDesdeResp ?? this.usuarioSubject.value,
          );

         
        }),
      );
  }

  setGrupos(grupos: any[] | null | undefined): void {
    const lista = Array.isArray(grupos) ? grupos : [];
    this.gruposSubject.next(lista);
    this.guardarEnStorage(
      this.permisosSubject.value,
      this.empresaIdActual,
      lista,
      this.usuarioSubject.value,
    );
  }

  getGruposActuales(): any[] {
    return this.gruposSubject.value;
  }

  setUsuario(usuario: any | null | undefined): void {
    const sanitized = this.sanitizarUsuario(usuario);
    this.usuarioSubject.next(sanitized);
    this.guardarEnStorage(
      this.permisosSubject.value,
      this.empresaIdActual,
      this.gruposSubject.value,
      sanitized,
    );
  }

  getUsuarioActual(): any | null {
    return this.usuarioSubject.value;
  }

  limpiar(): void {
    this.empresaIdActual = null;
    this.permisosSubject.next(null);
    this.gruposSubject.next([]);
    this.usuarioSubject.next(null);
    this.limpiarStorage();
  }

  tienePermiso(modulo: string, accion: PermisoAccion = 'VER'): boolean {
    if (this.esAdminBlister()) {
      return true;
    }

    const permisos = this.permisosSubject.value;
    if (!permisos) {
      return false;
    }

    const normalize = (v: string) => v.toLowerCase().replace(/[_\s]+/g, '-');
    const target = normalize(modulo);
    const permisosMap = permisos.permisos ?? {};
    const key = Object.keys(permisosMap).find((k) => normalize(k) === target);

    return (key ? permisosMap[key]?.[accion] : undefined) ?? false;
  }

  private esAdminBlister(): boolean {
    const grupos = this.gruposSubject.value;
    if (!Array.isArray(grupos) || !grupos.length) {
      return false;
    }

    const normalize = (v: any) =>
      String(v ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();

    return grupos.some((g: any) => {
      const codigo = normalize(g?.grupo?.codigo ?? g?.codigo);
      return codigo === PermisosStateService.ADMIN_BLISTER_GROUP_CODE;
    });
  }

  getPermisosActuales(): PermisoSistema | null {
    return this.permisosSubject.value;
  }

  get sistemaCodigoActual(): SistemaCodigo | null {
    return this.permisosSubject.value?.sistema.codigo ?? null;
  }

  get empresaIdContexto(): string | null {
    return this.empresaIdActual;
  }

  getSistemaActual(): { id: number; codigo: SistemaCodigo; nombre: string } | null {
    const permisos = this.permisosSubject.value;
    if (!permisos?.sistema) {
      return null;
    }
    return permisos.sistema;
  }

  private guardarEnStorage(
    permisos: PermisoSistema | null,
    empresaId: string | null,
    grupos: any[] = [],
    usuario: any | null = null,
  ): void {
    this.permisosSubject.next(permisos);
    this.gruposSubject.next(Array.isArray(grupos) ? grupos : []);
    const usuarioSanitizado = this.sanitizarUsuario(usuario);
    this.usuarioSubject.next(usuarioSanitizado);
    try {
      const payload = {
        permisos,
        empresaId,
        grupos: Array.isArray(grupos) ? grupos : [],
        usuario: usuarioSanitizado,
      };
      sessionStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch (e) {
      // ignore storage errors
    }
  }

  private hidratarDesdeStorage(): void {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        permisos?: PermisoSistema;
        empresaId?: string | null;
        grupos?: any[];
        usuario?: any;
      };
      if (!parsed?.permisos) return;

      const sistemaActual = this.sistemaContext.sistemaActual;
      const codigoCache = parsed.permisos?.sistema?.codigo ?? null;

      if (!sistemaActual || !codigoCache || sistemaActual !== codigoCache) {
        this.limpiarStorage();
        return;
      }

      this.empresaIdActual = parsed.empresaId ?? null;
      this.permisosSubject.next(parsed.permisos);
      this.gruposSubject.next(Array.isArray(parsed.grupos) ? parsed.grupos : []);
      const usuarioSanitizado = this.sanitizarUsuario(parsed.usuario);
      this.usuarioSubject.next(usuarioSanitizado);
    
    } catch (e) {
      this.limpiarStorage();
    }
  }

  private sanitizarUsuario(usuario: any): any | null {
    if (!usuario || typeof usuario !== 'object') return null;
    return {
      id: usuario.id ?? null,
      username: usuario.username ?? null,
      nombre: usuario.nombre ?? null,
      apellido: usuario.apellido ?? null,
      correo: usuario.correo ?? null,
    };
  }

  private extraerGruposDesdePermisos(payload: any): any[] {
    if (!payload || typeof payload !== 'object') return [];

    if (Array.isArray(payload.grupos)) return payload.grupos;
    if (Array.isArray(payload.usuarioGrupos)) return payload.usuarioGrupos;
    if (Array.isArray(payload.usuario?.usuarioGrupos)) return payload.usuario.usuarioGrupos;
    if (Array.isArray(payload.data?.grupos)) return payload.data.grupos;
    if (Array.isArray(payload.data?.usuarioGrupos)) return payload.data.usuarioGrupos;
    if (Array.isArray(payload.data?.usuario?.usuarioGrupos)) return payload.data.usuario.usuarioGrupos;

    return [];
  }

  private extraerUsuarioDesdePermisos(payload: any): any | null {
    if (!payload || typeof payload !== 'object') return null;

    if (payload.usuario && typeof payload.usuario === 'object') return payload.usuario;
    if (payload.data?.usuario && typeof payload.data.usuario === 'object') return payload.data.usuario;

    return null;
  }

  private limpiarStorage(): void {
    try {
      sessionStorage.removeItem(this.storageKey);
    } catch (e) {
      // ignore storage errors
    }
  }
}
